import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Slider } from '@/components/ui/slider';
import { useToast } from '@/hooks/use-toast';
import { useEnhancedSessionGeneration } from '@/hooks/useEnhancedSessionGeneration';
import { usePractitionerShortsUsage } from '@/hooks/usePractitionerShortsUsage';
import { useVoices, VoiceOption } from "@/hooks/useVoices";
import { useBackgroundMusicOptions } from "@/hooks/useBackgroundMusic";
import { categoryConfigs } from '@/config/categories';
import { backendClient } from '@/lib/backendClient';
import EnhancedAudioPlayer from '@/components/EnhancedAudioPlayer';
import { Play, Video, Clock, Zap, Users, Volume2, Music, Mic, AlertTriangle, DollarSign, Save, Share2 } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';

interface ShortsTemplate {
  id: string;
  name: string;
  prompt: string;
  category: string;
  hook: string;
  recommendedVoice?: string;
  recommendedMusic?: string;
}

// All available voices will be fetched from the hook

// Generate dynamic templates based on categories (subset of admin templates)
const generateCategoryTemplates = (): ShortsTemplate[] => {
  const baseTemplates: ShortsTemplate[] = [];
  
  // Add templates for main categories only
  const mainCategories = ['meditation', 'motivation', 'sleep', 'anxiety', 'massage'];
  
  mainCategories.forEach(categoryId => {
    const config = categoryConfigs[categoryId];
    if (!config?.name) return;
    
    const categoryName = config.name;
    const hooks = [
      `Transform your ${categoryName.toLowerCase()} in under a minute...`,
      `Discover the power of ${categoryName.toLowerCase()}...`,
      `Quick ${categoryName.toLowerCase()} boost in 30 seconds...`,
    ];
    
    const hook = hooks[Math.floor(Math.random() * hooks.length)];
    
    baseTemplates.push({
      id: `${categoryId}-hook`,
      name: `${categoryName} Hook`,
      category: categoryId,
      hook,
      prompt: `Create a compelling ${categoryName.toLowerCase()} micro-session. Start with "${hook}" and provide one powerful technique. End with "Experience more with VoiceThera."`,
      recommendedMusic: categoryId === 'motivation' ? 'stomp-motivation.mp3' : 
                       categoryId === 'sleep' ? 'sounds_of_nature.mp3' : 
                       'universal_background.mp3',
      recommendedVoice: categoryId === 'motivation' ? 'male_coach' : 
                       categoryId === 'sleep' ? 'female_whisspy' : 
                       categoryId === 'massage' ? 'female_sleepish' : undefined
    });
  });
  
  return baseTemplates;
};

const SHORT_DURATIONS = [
  { value: '15 seconds', label: '15 seconds', icon: '⚡' },
  { value: '30 seconds', label: '30 seconds', icon: '🎯' },
  { value: '45 seconds', label: '45 seconds', icon: '⭐' },
  { value: '1 minute', label: '1 minute', icon: '💫' }
];

// Available categories for quick selection
const AVAILABLE_CATEGORIES = Object.entries(categoryConfigs).slice(0, 8).map(([id, config]) => ({
  id,
  name: config.name || id,
  description: config.description || `${config.name || id} sessions`
}));

const PractitionerShortsGenerator: React.FC = () => {
  const { voices } = useVoices();
  const allVoices = voices ? [...voices.male, ...voices.female] : [];
  const { options: backgroundMusicOptions } = useBackgroundMusicOptions();

  const [selectedDuration, setSelectedDuration] = useState<string>('30 seconds');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [customPrompt, setCustomPrompt] = useState<string>('');
  const [selectedVoice, setSelectedVoice] = useState<string>('female_default');
  const [selectedCategory, setSelectedCategory] = useState<string>('meditation');
  const [selectedMusic, setSelectedMusic] = useState<string>('universal_background.mp3');
  const [currentPreviewMusic, setCurrentPreviewMusic] = useState<string>('universal_background.mp3');
  const [musicVolume, setMusicVolume] = useState<number>(30);
  const [previewingVoice, setPreviewingVoice] = useState<string | null>(null);
  const [previewingMusic, setPreviewingMusic] = useState<string | null>(null);
  const [currentMusicAudio, setCurrentMusicAudio] = useState<HTMLAudioElement | null>(null);
  const [currentVoiceAudio, setCurrentVoiceAudio] = useState<HTMLAudioElement | null>(null);
  const [shortsTemplates] = useState<ShortsTemplate[]>(generateCategoryTemplates());
  const [templateRecommendedMusic, setTemplateRecommendedMusic] = useState<string | undefined>(undefined);
  const [generatedShorts, setGeneratedShorts] = useState<Array<{ 
    id: string; 
    duration: string; 
    audioUrl: string; 
    content: string; 
    template: string;
    voice: string;
    music: string;
    musicFile: string;
    category: string;
  }>>([]);

  const { toast } = useToast();
  const { 
    generateAudioFromPrompt, 
    isGenerating, 
    audioUrl, 
    generatedContent,
    resetAudio,
    currentStep 
  } = useEnhancedSessionGeneration();

  const {
    usage,
    isLoading: usageLoading,
    incrementUsage,
    isIncrementing,
    dailyLimit,
    overageRate,
    freeRemaining,
    generatedToday,
    overageChargesTotal,
    canGenerate
  } = usePractitionerShortsUsage();

  const handleTemplateSelect = (templateId: string) => {
    console.log('🎵 Template selected:', templateId);
    const template = shortsTemplates.find(t => t.id === templateId);
    if (!template) {
      console.log('🎵 Template not found for ID:', templateId);
      return;
    }

    setSelectedTemplate(templateId);
    setCustomPrompt(template.prompt);

    if (template.category) setSelectedCategory(template.category);
    if (template.recommendedVoice) setSelectedVoice(template.recommendedVoice);

    // Do NOT override user-selected music. Store recommendation for optional apply.
    setTemplateRecommendedMusic(template.recommendedMusic);
    if (template.recommendedMusic && template.recommendedMusic !== selectedMusic) {
      console.log('🎵 Template recommends music:', template.recommendedMusic, '— keeping current selection:', selectedMusic);
    }
  };

  const handleMusicChange = (musicId: string) => {
    console.log('🎵 DEBUG PractitionerShortsGenerator - Music selection changed to:', musicId);
    setSelectedMusic(musicId);
  };

  const playVoicePreview = async (voiceId: string) => {
    // Stop any currently playing voice
    if (currentVoiceAudio) {
      currentVoiceAudio.pause();
      setCurrentVoiceAudio(null);
    }

    if (previewingVoice === voiceId) {
      setPreviewingVoice(null);
      return;
    }

    try {
      setPreviewingVoice(voiceId);
      const voice = allVoices.find(v => v.id === voiceId);
      
      // Simple predefined voice samples - immediate playback like music preview
      const voiceSamples: Record<string, string> = {
        'female_default': '/voice-samples/sophia-preview.mp3',
        'female_secondary': '/voice-samples/isabella-preview.mp3', 
        'female_coachess': '/voice-samples/coachess-preview.mp3',
        'female_whisspy': '/voice-samples/whisspy-preview.mp3',
        'female_sleepish': '/voice-samples/sleepish-preview.mp3',
        'female_custom': '/voice-samples/michele-preview.mp3',
        'male_default': '/voice-samples/marcus-preview.mp3',
        'male_secondary': '/voice-samples/george-preview.mp3',
        'male_alexander': '/voice-samples/alexander-preview.mp3',
        'male_mike': '/voice-samples/mike-preview.mp3',
        'male_coach': '/voice-samples/coach-preview.mp3'
      };

      const sampleUrl = voiceSamples[voiceId];
      if (sampleUrl) {
        const audio = new Audio(sampleUrl);
        setCurrentVoiceAudio(audio);
        
        audio.addEventListener('ended', () => {
          setPreviewingVoice(null);
          setCurrentVoiceAudio(null);
        });
        
        audio.onerror = () => {
          console.log(`Voice sample file not found: ${sampleUrl}, using text preview`);
          setPreviewingVoice(null);
          toast({
            title: "Voice Preview",
            description: `${voice?.name || 'Voice'} sample not available. Voice will work in final generation.`,
            variant: "default"
          });
        };
        
        audio.play();
      } else {
        setPreviewingVoice(null);
        toast({
          title: "Voice Preview",
          description: `${voice?.name || 'Voice'} preview not available. Voice will work in final generation.`,
          variant: "default"
        });
      }
    } catch (error) {
      console.error('Voice preview failed:', error);
      setPreviewingVoice(null);
    }
  };

  const playMusicPreview = async (musicId: string) => {
    // Stop any currently playing music
    if (currentMusicAudio) {
      currentMusicAudio.pause();
      setCurrentMusicAudio(null);
    }

    if (previewingMusic === musicId) {
      setPreviewingMusic(null);
      return;
    }

    try {
      setPreviewingMusic(musicId);
      const { data } = await backendClient.storage
        .from('audio-sessions')
        .getPublicUrl(`backgrounds/${musicId}`);

      if (data?.publicUrl) {
        const audio = new Audio(data.publicUrl);
        audio.volume = musicVolume / 100;
        
        // Store reference to current audio
        setCurrentMusicAudio(audio);
        
        audio.addEventListener('ended', () => {
          setPreviewingMusic(null);
          setCurrentMusicAudio(null);
        });
        
        audio.onerror = () => {
          console.error(`Background music file not found: ${musicId}`);
          setPreviewingMusic(null);
          setCurrentMusicAudio(null);
          toast({
            title: "Music Preview Failed",
            description: `Background music "${musicId}" not found.`,
            variant: "destructive",
          });
        };

        audio.play();
        
        // Auto-stop after 10 seconds
        setTimeout(() => {
          if (audio && !audio.paused) {
            audio.pause();
            setPreviewingMusic(null);
            setCurrentMusicAudio(null);
          }
        }, 10000);
      }
    } catch (error) {
      console.error('Music preview failed:', error);
      setPreviewingMusic(null);
      setCurrentMusicAudio(null);
    }
  };

  const generateShort = async () => {
    if (!customPrompt.trim()) {
      toast({
        title: "Missing Prompt",
        description: "Please enter a prompt or select a template.",
        variant: "destructive",
      });
      return;
    }

    // Warn about overage charges
    if (freeRemaining <= 0) {
      const confirmed = window.confirm(
        `You've used all 10 free shorts today. Generating this short will cost ${formatCurrency(overageRate)}. Continue?`
      );
      if (!confirmed) return;
    }

    try {
      // Increment usage first to track billing
      incrementUsage();

      const selectedVoiceData = allVoices.find(v => v.id === selectedVoice);
      const selectedMusicData = backgroundMusicOptions.find(m => m.id === selectedMusic);
      
      console.log('🎵 DEBUG PractitionerShortsGenerator - Selected music state:', selectedMusic);
      console.log('🎵 DEBUG PractitionerShortsGenerator - Selected music data:', selectedMusicData);
      
      // Freeze current preview music to avoid later UI changes affecting playback
      setCurrentPreviewMusic(selectedMusic);
      console.log('🎵 DEBUG PractitionerShortsGenerator - currentPreviewMusic set to:', selectedMusic);
      
      const finalPrompt = `${customPrompt}\n\nSession Length: ${selectedDuration}\nVoice: ${selectedVoiceData?.name || 'Default'}\nBackground Music: ${selectedMusicData?.name || 'Ambient'}\nCategory: ${selectedCategory}\nOptimized for social media sharing.`;
      
      await generateAudioFromPrompt(
        finalPrompt,
        false, // useExactText
        false, // includeBiography
        '', // userBiography
        selectedCategory, // categoryId
        selectedVoiceData?.id.includes('male') ? 'male' : 'female', // voiceGender
        `Social Short - ${selectedDuration}`, // sessionTitle
        'standard', // voiceType
        'shorts', // sessionType
        undefined, // latencyTolerance
        undefined, // groupSession
        false, // usePersonalBiography
        undefined, // practitionerClient
        selectedVoice, // specific_voice_id - pass the actual voice ID
        undefined, // practitionerProfile
        false, // fastMode
        selectedMusic // backgroundMusicFile - pass the selected background music
      );

      toast({
        title: "Short Generated!",
        description: `${selectedDuration} social media short created successfully.`,
      });

    } catch (error: any) {
      toast({
        title: "Generation Failed",
        description: error.message || "Failed to generate short.",
        variant: "destructive",
      });
    }
  };

  const saveGenerated = () => {
    if (audioUrl && generatedContent) {
      const selectedVoiceData = allVoices.find(v => v.id === selectedVoice);
      const selectedMusicData = backgroundMusicOptions.find(m => m.id === selectedMusic);
      
      const newShort = {
        id: Date.now().toString(),
        duration: selectedDuration,
        audioUrl,
        content: generatedContent,
        template: selectedTemplate || 'custom',
        voice: selectedVoiceData?.name || 'Default',
        music: selectedMusicData?.name || 'Ambient', // display name
        musicFile: selectedMusic, // actual file id for playback
        category: selectedCategory
      };
      setGeneratedShorts(prev => [newShort, ...prev]);
      resetAudio();
      setCustomPrompt('');
      setSelectedTemplate('');
      
      toast({
        title: "Short Saved",
        description: "Short added to your collection.",
      });
    }
  };

  const shareAudio = async (audioUrl: string) => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'VoiceThera Short',
          text: 'Check out this meditation short from VoiceThera',
          url: audioUrl,
        });
      } else {
        await navigator.clipboard.writeText(audioUrl);
        toast({
          title: "Link Copied",
          description: "Audio URL copied to clipboard for sharing.",
        });
      }
    } catch (error) {
      console.error('Share failed:', error);
    }
  };

  const progressPercentage = (generatedToday / dailyLimit) * 100;
  const willIncurCharge = freeRemaining <= 0;

  if (usageLoading) {
    return <div className="p-6">Loading shorts usage...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-6">
        <Video className="h-6 w-6 text-primary" />
        <div>
          <h2 className="text-2xl font-bold">Social Media Shorts Generator</h2>
          <p className="text-muted-foreground">Create engaging 15s-1min audio content for TikTok & Instagram</p>
        </div>
      </div>

      {/* Usage Tracking Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Video className="h-5 w-5" />
            <span>Daily Usage</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span>Shorts Generated Today: {generatedToday}/{dailyLimit}</span>
                <span>{freeRemaining} free remaining</span>
              </div>
              <Progress value={progressPercentage} className="h-2" />
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center space-x-2">
                <Zap className="h-4 w-4 text-green-500" />
                <span>Free: 10/day</span>
              </div>
              <div className="flex items-center space-x-2">
                <DollarSign className="h-4 w-4 text-orange-500" />
                <span>Overage: {formatCurrency(overageRate)}/short</span>
              </div>
            </div>

            {overageChargesTotal > 0 && (
              <Alert>
                <DollarSign className="h-4 w-4" />
                <AlertDescription>
                  Today's overage charges: {formatCurrency(overageChargesTotal)}
                </AlertDescription>
              </Alert>
            )}

            {willIncurCharge && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  You've used all free shorts today. Each additional short costs {formatCurrency(overageRate)}.
                </AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Generator Controls */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Quick Setup
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Duration Selection */}
              <div>
                <label className="text-sm font-medium mb-2 block">Duration</label>
                <div className="grid grid-cols-4 gap-2">
                  {SHORT_DURATIONS.map((duration) => (
                    <Button
                      key={duration.value}
                      variant={selectedDuration === duration.value ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedDuration(duration.value)}
                      className="flex items-center gap-1"
                    >
                      <span>{duration.icon}</span>
                      {duration.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Category Selection */}
              <div>
                <label className="text-sm font-medium mb-2 block">Category</label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AVAILABLE_CATEGORIES.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Voice Selection */}
              <div>
                <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                  <Mic className="h-4 w-4" />
                  Voice Selection
                </label>
                <div className="space-y-2">
                  <Select value={selectedVoice} onValueChange={setSelectedVoice}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <div className="px-2 py-1 text-xs font-medium text-muted-foreground">Male Voices</div>
                      {MALE_VOICES.map((voice) => (
                        <SelectItem key={voice.id} value={voice.id}>
                          <div className="flex items-center justify-between w-full">
                            <div className="flex items-center space-x-2">
                              <span>👨</span>
                              <span>{voice.name}</span>
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                      <Separator />
                      <div className="px-2 py-1 text-xs font-medium text-muted-foreground">Female Voices</div>
                      {FEMALE_VOICES.map((voice) => (
                        <SelectItem key={voice.id} value={voice.id}>
                          <div className="flex items-center justify-between w-full">
                            <div className="flex items-center space-x-2">
                              <span>👩</span>
                              <span>{voice.name}</span>
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => playVoicePreview(selectedVoice)}
                    disabled={previewingVoice === selectedVoice}
                    className="w-full"
                  >
                    {previewingVoice === selectedVoice ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-3 w-3 border-2 border-primary border-t-transparent"></div>
                        Playing...
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Play className="h-3 w-3" />
                        Preview Voice
                      </div>
                    )}
                  </Button>
                </div>
              </div>

              {/* Background Music */}
              <div>
                <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                  <Music className="h-4 w-4" />
                  Background Music
                </label>
                <div className="space-y-2">
                  <Select value={selectedMusic} onValueChange={handleMusicChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {backgroundMusicOptions.map((music) => (
                        <SelectItem key={music.id} value={music.id}>
                          <div className="flex items-center space-x-2">
                            <span>🎵</span>
                            <span>{music.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => playMusicPreview(selectedMusic)}
                      disabled={previewingMusic === selectedMusic}
                      className="flex-1"
                    >
                      {previewingMusic === selectedMusic ? (
                        <div className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-3 w-3 border-2 border-primary border-t-transparent"></div>
                          Playing...
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Play className="h-3 w-3" />
                          Preview
                        </div>
                      )}
                    </Button>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Volume: {musicVolume}%</label>
                    <Slider
                      value={[musicVolume]}
                      onValueChange={(value) => setMusicVolume(value[0])}
                      max={100}
                      step={5}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Templates Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Quick Templates
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-2 max-h-64 overflow-y-auto">
                {shortsTemplates.map((template) => (
                  <Button
                    key={template.id}
                    variant={selectedTemplate === template.id ? "default" : "outline"}
                    className="justify-start h-auto p-3 text-left"
                    onClick={() => handleTemplateSelect(template.id)}
                  >
                    <div className="space-y-1">
                      <div className="font-medium">{template.name}</div>
                      <div className="text-xs text-muted-foreground line-clamp-2">
                        {template.hook}
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {template.category}
                      </Badge>
                    </div>
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Custom Prompt Section */}
          <Card>
            <CardHeader>
              <CardTitle>Custom Content</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Custom Prompt</label>
                <Textarea
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  placeholder="Enter your custom prompt or select a template above..."
                  rows={4}
                />
              </div>

              <Button 
                onClick={generateShort} 
                disabled={isGenerating || !canGenerate}
                className="w-full"
                size="lg"
              >
                {isGenerating ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-background border-t-transparent"></div>
                    <span>Generating... ({currentStep})</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <Video className="h-4 w-4" />
                    <span>
                      Generate Short 
                      {willIncurCharge && ` (${formatCurrency(overageRate)})`}
                    </span>
                  </div>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Preview & Results */}
        <div className="space-y-6">
          {audioUrl && (
            <Card>
              <CardHeader>
                <CardTitle>Generated Short</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <EnhancedAudioPlayer 
                  audioUrl={audioUrl} 
                />
                <div className="text-xs text-muted-foreground mb-2">
                  Duration: {selectedDuration}
                </div>
                <div className="p-3 bg-muted rounded-md">
                  <p className="text-sm">{generatedContent}</p>
                </div>
                <div className="flex space-x-2">
                  <Button onClick={saveGenerated} className="flex-1">
                    <Save className="h-4 w-4 mr-2" />
                    Save Short
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {generatedShorts.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Your Shorts Collection ({generatedShorts.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {generatedShorts.map((short) => (
                    <div key={short.id} className="p-3 border rounded-lg space-y-2">
                      <div className="flex items-center justify-between">
                        <Badge variant="secondary">{short.duration}</Badge>
                        <Badge variant="outline">{short.category}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {short.content}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-muted-foreground">
                          {short.voice} • {short.music}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => shareAudio(short.audioUrl)}
                        >
                          <Share2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default PractitionerShortsGenerator;